import { ArtistI, NFTI } from '@tacans/shared-marketplace-layer';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import React, { useEffect, useMemo } from 'react';
import { generatePath, RouteE } from 'src/constants/pages.constants';
import { WalletColumn } from 'src/features/common/activity/components/activity-row/wallet-column';
import { useNftCollection } from 'src/features/common/collectible/hooks/collectible.hooks';
import { INftMiscLocale, INftUserRoleLocale } from 'src/features/common/collectible/types/locales';
import { CollectionListLabel } from 'src/features/common/collection/components/collection-list-label';
import { useFetchLaunchpad, useLaunchpadById } from 'src/features/common/launchpad/hooks/use-lanchpads';
import { useProfileLoginUrl } from 'src/features/common/profile/hooks/use-profile-login-url';
import { useDisplayName } from 'src/features/common/user/hooks/use-display-name.hooks';
import { useUserListEntities } from 'src/features/common/user/hooks/user-list.hooks';
import { useMyWalletsById } from 'src/features/common/wallet/hooks/use-my-wallets-by-id';
import { Grid } from 'src/ui/common/atoms/grid';
import { ProtectedLink } from 'src/ui/common/atoms/protected-link';
import { Typography } from 'src/ui/common/atoms/typography';
import { CardBox } from 'src/ui/white-labels/ocean/layouts/card-box';
import { decodeHTMLEntities } from 'src/utils/html.utils';
import { getNftTypeByStandard } from 'src/utils/lot.utils';
import { StyledItem, StyledItemValue } from './nft-collection-container.styles';

export const NftCollectionOwnerContainer: React.VFC<{ nft: NFTI; artist?: ArtistI }> = ({ nft, artist }) => {
    const collection = useNftCollection();
    const { t } = useTranslation('pages');
    const users = useUserListEntities();
    const { isSingle } = getNftTypeByStandard(nft.standard);
    const fetchLaunchpad = useFetchLaunchpad();
    const launchpad = useLaunchpadById(nft.launchpadId);
    const myWallets = useMyWalletsById();
    const isOwner = !!myWallets[nft.owner];

    const locale = useMemo(
        () => ({
            misc: t('nft.view.misc', { returnObjects: true }) as INftMiscLocale,
            role: t('nft.view.userRoles', { returnObjects: true }) as INftUserRoleLocale,
        }),
        [t]
    );

    const ownerName = useDisplayName(users[nft.owner]);
    const creatorName = useDisplayName(users[nft.author]);

    const ownerUrl = useProfileLoginUrl(users[nft.owner]);
    const creatorUrl = useProfileLoginUrl(users[nft.author]);
    const artistUrl = artist && generatePath(RouteE.CREATOR_PROFILE, { id: artist.id });
    const launchpadUrl = launchpad && generatePath(RouteE.LAUNCHPAD_DETAILS, { id: launchpad.id });

    useEffect(() => {
        isOwner && nft.launchpadId && fetchLaunchpad(nft.launchpadId);
    }, [isOwner, nft.launchpadId, fetchLaunchpad]);

    return (
        <CardBox>
            <CardBox.Content>
                <Grid xs={2} sm={1} lg={artist ? 4 : 3} rowGap={15}>
                    {collection && (
                        <StyledItem>
                            <Typography tag="div" color="blue" semiBold size="xs">
                                {locale.misc.collection}
                            </Typography>
                            <CollectionListLabel collection={collection} />
                        </StyledItem>
                    )}
                    {isOwner && launchpad && (
                        <StyledItem>
                            <Typography tag="div" color="blue" semiBold size="xs">
                                {locale.misc.launchpad}
                            </Typography>
                            <StyledItemValue>
                                <Link href={launchpadUrl!} prefetch={false}>
                                    <a>
                                        <Typography variant="text" size="xs" sizeSm="sm">
                                            {decodeHTMLEntities(launchpad.name)}
                                        </Typography>
                                    </a>
                                </Link>
                            </StyledItemValue>
                        </StyledItem>
                    )}
                    {isSingle && nft.copies && (
                        <StyledItem>
                            <Typography tag="div" color="blue" semiBold size="xs">
                                {locale.role.owner}
                            </Typography>
                            <StyledItemValue>
                                <ProtectedLink disabled={!ownerName} href={ownerUrl} prefetch={false}>
                                    {ownerName ? (
                                        <a>
                                            <Typography variant="text" size="xs" sizeSm="sm">
                                                {decodeHTMLEntities(ownerName)}
                                            </Typography>
                                        </a>
                                    ) : (
                                        <WalletColumn text={nft.owner} />
                                    )}
                                </ProtectedLink>
                            </StyledItemValue>
                        </StyledItem>
                    )}
                    <StyledItem>
                        <Typography tag="div" color="blue" semiBold size="xs">
                            {locale.role.author}
                        </Typography>
                        <StyledItemValue>
                            <Link href={creatorUrl} prefetch={false}>
                                <a>
                                    <Typography variant="text" size="xs" sizeSm="sm">
                                        {decodeHTMLEntities(creatorName)}
                                    </Typography>
                                </a>
                            </Link>
                        </StyledItemValue>
                    </StyledItem>
                    {artist && (
                        <StyledItem>
                            <Typography tag="div" color="blue" semiBold size="xs">
                                {locale.role.creator}
                            </Typography>
                            <StyledItemValue>
                                <Link href={artistUrl!} prefetch={false}>
                                    <a>
                                        <Typography variant="text" size="xs" sizeSm="sm">
                                            {artist.displayName}
                                        </Typography>
                                    </a>
                                </Link>
                            </StyledItemValue>
                        </StyledItem>
                    )}
                    {+(nft.copies ?? 0) > 0 && (
                        <StyledItem>
                            <Typography tag="div" color="blue" semiBold size="xs">
                                {locale.misc.editions}
                            </Typography>
                            <StyledItemValue>
                                <Typography variant="text" size="xs" sizeSm="sm">
                                    {nft.copies}
                                </Typography>
                            </StyledItemValue>
                        </StyledItem>
                    )}
                </Grid>
            </CardBox.Content>
        </CardBox>
    );
};
